/**
 * SQLite Database Schema for Offline Journey Support
 *
 * This schema mirrors the server-side PostgreSQL schema but simplified
 * for mobile offline use. It includes sync metadata for conflict resolution.
 */

export interface LocalJourney {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  skill_level: 'beginner' | 'intermediate' | 'advanced';
  focus_areas: string; // JSON string array
  duration_weeks: number;
  time_commitment_hours_per_week: number;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  current_week: number;
  progress_percentage: number;
  learning_style?: string;
  preferred_content_types: string; // JSON string array
  difficulty_preference?: string;
  created_at: string; // ISO string
  started_at?: string;
  completed_at?: string;
  last_activity_at?: string;
  updated_at: string;
  // Sync metadata
  last_sync_at?: string;
  needs_sync: boolean;
  sync_version: number;
}

export interface LocalJourneyWeek {
  id: string;
  journey_id: string;
  week_number: number;
  title: string;
  description?: string;
  learning_objectives: string; // JSON string array
  estimated_hours?: number;
  is_unlocked: boolean;
  is_completed: boolean;
  completion_date?: string;
  created_at: string;
  updated_at: string;
  // Sync metadata
  last_sync_at?: string;
  needs_sync: boolean;
  sync_version: number;
}

export interface LocalJourneyActivity {
  id: string;
  week_id: string;
  title: string;
  description?: string;
  activity_type: 'video' | 'recipe' | 'reading' | 'quiz' | 'practice';
  registry_id?: string;
  estimated_minutes?: number;
  difficulty_level?: number;
  required_equipment: string; // JSON string array
  order_index: number;
  prerequisites: string; // JSON string array
  is_completed: boolean;
  completion_date?: string;
  user_rating?: number;
  user_feedback?: string;
  created_at: string;
  updated_at: string;
  // Sync metadata
  last_sync_at?: string;
  needs_sync: boolean;
  sync_version: number;
}

export interface LocalActivityProgress {
  id: string;
  user_id: string;
  activity_id: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'skipped';
  progress_percentage: number;
  attempts: number;
  time_spent_minutes: number;
  success_rate?: number;
  engagement_score?: number;
  difficulty_rating?: number;
  started_at?: string;
  completed_at?: string;
  last_accessed_at: string;
  // Sync metadata
  last_sync_at?: string;
  needs_sync: boolean;
  sync_version: number;
  operation_id?: string; // For idempotency
}

export interface LocalContentRegistry {
  id: string;
  kind: 'recipe' | 'video' | 'article' | 'quiz';
  source_id: string;
  source_table: string;
  title: string;
  author?: string;
  duration_minutes?: number;
  url?: string;
  metadata: string; // JSON string
  created_at: string;
  updated_at: string;
  // Offline content metadata
  is_downloaded: boolean;
  local_path?: string;
  download_size?: number;
  last_accessed_at?: string;
}

export interface LocalSyncLog {
  id: string;
  table_name: string;
  record_id: string;
  operation: 'create' | 'update' | 'delete';
  sync_status: 'pending' | 'syncing' | 'completed' | 'failed';
  retry_count: number;
  last_attempt_at?: string;
  error_message?: string;
  created_at: string;
  operation_id?: string;
}

// Database schema creation SQL
export const CREATE_TABLES_SQL = `
  -- Journeys table
  CREATE TABLE IF NOT EXISTS journeys (
    id TEXT PRIMARY KEY,
    user_id TEXT NOT NULL,
    title TEXT NOT NULL,
    description TEXT,
    skill_level TEXT NOT NULL CHECK (skill_level IN ('beginner', 'intermediate', 'advanced')),
    focus_areas TEXT DEFAULT '[]',
    duration_weeks INTEGER NOT NULL,
    time_commitment_hours_per_week REAL NOT NULL,
    status TEXT NOT NULL DEFAULT 'active' CHECK (status IN ('active', 'paused', 'completed', 'abandoned')),
    current_week INTEGER DEFAULT 1,
    progress_percentage REAL DEFAULT 0.0,
    learning_style TEXT,
    preferred_content_types TEXT DEFAULT '[]',
    difficulty_preference TEXT,
    created_at TEXT NOT NULL,
    started_at TEXT,
    completed_at TEXT,
    last_activity_at TEXT,
    updated_at TEXT NOT NULL,
    last_sync_at TEXT,
    needs_sync INTEGER DEFAULT 0,
    sync_version INTEGER DEFAULT 1
  );

  -- Journey weeks table
  CREATE TABLE IF NOT EXISTS journey_weeks (
    id TEXT PRIMARY KEY,
    journey_id TEXT NOT NULL,
    week_number INTEGER NOT NULL,
    title TEXT NOT NULL,
    description TEXT,
    learning_objectives TEXT DEFAULT '[]',
    estimated_hours REAL,
    is_unlocked INTEGER DEFAULT 0,
    is_completed INTEGER DEFAULT 0,
    completion_date TEXT,
    created_at TEXT NOT NULL,
    updated_at TEXT NOT NULL,
    last_sync_at TEXT,
    needs_sync INTEGER DEFAULT 0,
    sync_version INTEGER DEFAULT 1,
    FOREIGN KEY (journey_id) REFERENCES journeys (id) ON DELETE CASCADE
  );

  -- Journey activities table
  CREATE TABLE IF NOT EXISTS journey_activities (
    id TEXT PRIMARY KEY,
    week_id TEXT NOT NULL,
    title TEXT NOT NULL,
    description TEXT,
    activity_type TEXT NOT NULL CHECK (activity_type IN ('video', 'recipe', 'reading', 'quiz', 'practice')),
    registry_id TEXT,
    estimated_minutes INTEGER,
    difficulty_level REAL,
    required_equipment TEXT DEFAULT '[]',
    order_index INTEGER NOT NULL,
    prerequisites TEXT DEFAULT '[]',
    is_completed INTEGER DEFAULT 0,
    completion_date TEXT,
    user_rating INTEGER CHECK (user_rating BETWEEN 1 AND 5),
    user_feedback TEXT,
    created_at TEXT NOT NULL,
    updated_at TEXT NOT NULL,
    last_sync_at TEXT,
    needs_sync INTEGER DEFAULT 0,
    sync_version INTEGER DEFAULT 1,
    FOREIGN KEY (week_id) REFERENCES journey_weeks (id) ON DELETE CASCADE
  );

  -- Activity progress table
  CREATE TABLE IF NOT EXISTS activity_progress (
    id TEXT PRIMARY KEY,
    user_id TEXT NOT NULL,
    activity_id TEXT NOT NULL,
    status TEXT NOT NULL DEFAULT 'not_started' CHECK (status IN ('not_started', 'in_progress', 'completed', 'skipped')),
    progress_percentage REAL DEFAULT 0.0,
    attempts INTEGER DEFAULT 0,
    time_spent_minutes INTEGER DEFAULT 0,
    success_rate REAL,
    engagement_score REAL,
    difficulty_rating INTEGER CHECK (difficulty_rating BETWEEN 1 AND 5),
    started_at TEXT,
    completed_at TEXT,
    last_accessed_at TEXT NOT NULL,
    last_sync_at TEXT,
    needs_sync INTEGER DEFAULT 0,
    sync_version INTEGER DEFAULT 1,
    operation_id TEXT,
    FOREIGN KEY (activity_id) REFERENCES journey_activities (id) ON DELETE CASCADE,
    UNIQUE(user_id, activity_id)
  );

  -- Content registry table
  CREATE TABLE IF NOT EXISTS content_registry (
    id TEXT PRIMARY KEY,
    kind TEXT NOT NULL CHECK (kind IN ('recipe', 'video', 'article', 'quiz')),
    source_id TEXT NOT NULL,
    source_table TEXT NOT NULL,
    title TEXT NOT NULL,
    author TEXT,
    duration_minutes INTEGER,
    url TEXT,
    metadata TEXT DEFAULT '{}',
    created_at TEXT NOT NULL,
    updated_at TEXT NOT NULL,
    is_downloaded INTEGER DEFAULT 0,
    local_path TEXT,
    download_size INTEGER,
    last_accessed_at TEXT,
    UNIQUE(kind, source_id)
  );

  -- Sync log table
  CREATE TABLE IF NOT EXISTS sync_log (
    id TEXT PRIMARY KEY,
    table_name TEXT NOT NULL,
    record_id TEXT NOT NULL,
    operation TEXT NOT NULL CHECK (operation IN ('create', 'update', 'delete')),
    sync_status TEXT NOT NULL DEFAULT 'pending' CHECK (sync_status IN ('pending', 'syncing', 'completed', 'failed')),
    retry_count INTEGER DEFAULT 0,
    last_attempt_at TEXT,
    error_message TEXT,
    created_at TEXT NOT NULL,
    operation_id TEXT
  );

  -- Indexes for performance
  CREATE INDEX IF NOT EXISTS idx_journeys_user_id ON journeys(user_id);
  CREATE INDEX IF NOT EXISTS idx_journeys_status ON journeys(status);
  CREATE INDEX IF NOT EXISTS idx_journeys_needs_sync ON journeys(needs_sync);
  
  CREATE INDEX IF NOT EXISTS idx_journey_weeks_journey_id ON journey_weeks(journey_id);
  CREATE INDEX IF NOT EXISTS idx_journey_weeks_needs_sync ON journey_weeks(needs_sync);
  
  CREATE INDEX IF NOT EXISTS idx_journey_activities_week_id ON journey_activities(week_id);
  CREATE INDEX IF NOT EXISTS idx_journey_activities_needs_sync ON journey_activities(needs_sync);
  
  CREATE INDEX IF NOT EXISTS idx_activity_progress_user_id ON activity_progress(user_id);
  CREATE INDEX IF NOT EXISTS idx_activity_progress_activity_id ON activity_progress(activity_id);
  CREATE INDEX IF NOT EXISTS idx_activity_progress_needs_sync ON activity_progress(needs_sync);
  
  CREATE INDEX IF NOT EXISTS idx_content_registry_kind ON content_registry(kind);
  
  CREATE INDEX IF NOT EXISTS idx_sync_log_status ON sync_log(sync_status);
  CREATE INDEX IF NOT EXISTS idx_sync_log_table_record ON sync_log(table_name, record_id);
`;
